import classNames from "classnames";
import React, { type ChangeEvent } from "react";
import { Svg } from "../../../../Shared/Assets/Svg/React";

interface SelectionItemProps {
  name: string;
  value: string;
  checked: boolean;
  labelTitle: string;
  labelSubTitle?: string;
  /** A short note below the title, eg the animals a treatment is for */
  labelNote?: string;
  disabled?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const SelectionItem = ({
  name,
  value,
  checked,
  labelTitle,
  labelSubTitle,
  labelNote,
  disabled = false,
  onChange
}: SelectionItemProps) => {
  const iconPath = disabled ? "/svg/toggle/marker.svg" : "/svg/toggle/dot.svg";

  const labelClasses = classNames(
    "relative overflow-hidden group/selectionitem",
    "flex gap-xs w-full cursor-pointer items-center transition-short-in-out",
    "rounded-sm p-xs"
  );

  const radioContainerClasses = classNames(
    "flex items-center px-md py-sm transition-short-in-out",
    "rounded-tl-br-sm aspect-square",
    "border focus-outline",
    "has-[input:focus-visible]:focus-outline",
    "has-[input:focus-visible]:outline",
    {
      "border-neutral-300 group-hover-[:has(input:checked)]/selectionitem:border-neutral-300":
        disabled,
      "border-turquoise-800 bg-turquoise-600": checked,
      "border-neutral-300": !checked
    }
  );

  const radioIndicatorClasses = classNames(
    "transition-short-in-out items-center justify-center border rounded-full",
    " bg-transparent text-transparent flex w-md h-md",
    {
      "border-turquoise-800 bg-turquoise-800 group-hover/selectionitem:border-turquoise-800 group-hover/selectionitem:bg-turquoise-800":
        checked,
      "group-focus-visible/selectionitem:border-turquoise-800 group-focus-visible/selectionitem:bg-turquoise-800":
        checked,
      "border-neutral-200": !checked
    }
  );

  const svgClasses = classNames("transition-short-in-out", {
    "h-md w-md": disabled,
    "h-xs w-xs": !disabled,
    "text-neutral-200": disabled,
    "group-hover/selectionitem:text-neutral-200 group-focus-visible/selectionitem:text-neutral-200":
      !checked && !disabled,
    "text-dark-blue group-hover/selectionitem:text-dark-blue": checked && !disabled
  });

  const subtitleClasses = classNames(
    "uppercase font-distinct-bold font-guardian text-distinct-xs md:text-distinct-sm",
    {
      "text-t-neutral-400": disabled,
      "text-t-neutral-800": checked && !disabled,
      "text-t-neutral-700": !checked && !disabled
    }
  );

  const titleClasses = classNames("text-md md:text-lg font-t-semi-bold", {
    "text-t-neutral-400": disabled,
    "text-dark-blue": !disabled
  });

  return (
    <label className={labelClasses}>
      <span className={radioContainerClasses}>
        <input
          type="radio"
          className="absolute -left-full peer"
          id={`${name}-${value}`}
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          {...(onChange ? { onChange } : { readOnly: true })}
        />
        <span className={radioIndicatorClasses}>
          <Svg href={iconPath} additionalClass={svgClasses} />
        </span>
      </span>
      <span className="flex flex-col gap-xxs grow transition-short-in-out">
        {labelSubTitle && <span className={subtitleClasses}>{labelSubTitle}</span>}
        <span className={titleClasses}>{labelTitle}</span>
        {labelNote && (
          <span
            className={classNames("text-t-sm", {
              "text-t-neutral-400": disabled,
              "text-t-neutral-700": !disabled
            })}
            data-online-booking-selection-note
          >
            {labelNote}
          </span>
        )}
      </span>
    </label>
  );
};

export default SelectionItem;
